/**
 * ADD ITEM PICKER MODAL
 *
 * Shows when user clicks "Add New Item" while viewing All Items.
 * Lets them choose which category of item to add.
 * Each button is styled with its category's theme color.
 */

import { X } from 'lucide-react';
import { AppIcons } from '../constants/icons';
import { CategoryColors } from '../constants/categoryColors';

interface AddItemPickerModalProps {
  onClose: () => void;
  onSelectCategory: (category: string) => void;
}

export default function AddItemPickerModal({ onClose, onSelectCategory }: AddItemPickerModalProps) {
  const categories = [
    {
      id: 'acrylic',
      label: 'Acrylic',
      icon: AppIcons.Acrylic,
      color: CategoryColors.acrylic.bgActive,
      description: 'Powders, monomers, dip systems'
    },
    {
      id: 'cuticle_care',
      label: 'Cuticle Care',
      icon: AppIcons.CuticleCare,
      color: CategoryColors.cuticle_care.bgActive,
      description: 'Oils, creams, serums & treatments'
    },
    {
      id: 'powder',
      label: 'Buffing Powder',
      icon: AppIcons.Powder,
      color: CategoryColors.powder.bgActive,
      description: 'Chrome, aurora, holographic & more'
    },
    {
      id: 'embellishment',
      label: 'Embellishment',
      icon: AppIcons.Embellishment,
      color: CategoryColors.embellishment.bgActive,
      description: 'Rhinestones, charms, studs & glitter'
    },
    {
      id: 'polish',
      label: 'Nail Polish',
      icon: AppIcons.Polish,
      color: CategoryColors.polish.bgActive,
      description: 'Lacquer, gel, dip base coats & more'
    },
    {
      id: 'plate',
      label: 'Stamping Plate',
      icon: AppIcons.Plate,
      color: CategoryColors.plate.bgActive,
      description: 'Metal plates with etched designs'
    },
    {
      id: 'tool',
      label: 'Tool',
      icon: AppIcons.Tool,
      color: CategoryColors.tool.bgActive,
      description: 'Brushes, stampers, lamps & more'
    },
    {
      id: 'transfer',
      label: 'Transfer / Decal',
      icon: AppIcons.Decal,
      color: CategoryColors.transfer.bgActive,
      description: 'Foils, stickers, waterslides & wraps'
    },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="glass-modal max-w-lg w-full shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-600">
          <h2 className="text-xl font-bold text-glass font-heading">
            What would you like to add?
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-slate-700/50 rounded-full transition-default text-glass-muted hover:text-glass"
          >
            <X className="icon-base" />
          </button>
        </div>

        {/* Category Grid */}
        <div className="p-5">
          <div className="grid grid-cols-2 gap-3">
            {categories.map((cat) => {
              const IconComponent = cat.icon;
              return (
                <button
                  key={cat.id}
                  onClick={() => onSelectCategory(cat.id)}
                  className={`${cat.color} text-white rounded-xl p-4 text-left transition-all transform hover:scale-[1.03] hover:shadow-lg active:scale-95`}
                >
                  <div className="flex items-center gap-2.5 mb-1.5">
                    <IconComponent className="icon-lg text-white flex-shrink-0" aria-label={cat.label} />
                    <span className="font-bold text-base font-body leading-tight">
                      {cat.label}
                    </span>
                  </div>
                  <p className="text-white/85 text-xs font-body leading-snug">
                    {cat.description}
                  </p>
                </button>
              );
            })}
          </div>

          {/* Cancel Button */}
          <button
            onClick={onClose}
            className="w-full mt-5 px-6 py-2.5 bg-slate-700/50 hover:bg-slate-700/80 text-glass-secondary hover:text-glass rounded-xl font-medium transition-default font-body"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
